import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import {
  CUSTOMER_REQUIREMENT_CATEGORY_KEYS,
  type CustomerRequirementCategory,
  type CustomerRequirementDto,
  type CustomerRequirementsInput,
} from "@hisense/shared";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { FileDropList, type DraftFile } from "@/components/docs/FileDropList";
import { formatDateTime } from "@/domain/datetime";

/** Một mục yêu cầu đang soạn. `localId` giữ khoá React ổn định cho mục chưa lưu. */
export interface RequirementDraft {
  id?: string;
  localId: string;
  category: CustomerRequirementCategory;
  title: string;
  description: string;
  photos: DraftFile[];
  updatedAt?: number;
  updatedByName?: string | null;
}

export const requirementsFromDto = (rows: CustomerRequirementDto[]): RequirementDraft[] =>
  rows.map((r) => ({
    id: r.id,
    localId: r.id,
    category: r.category,
    title: r.title ?? "",
    description: r.description ?? "",
    updatedAt: r.updatedAt,
    updatedByName: r.updatedByName,
    photos: r.photos.map((p) => ({ id: p.id, key: p.id, kind: "image" as const, fileName: p.fileName, fileSize: p.fileSize, mimeType: p.mimeType, url: p.url })),
  }));

/** Bản nháp → thân PUT: ảnh đã lưu chỉ gửi id (khoá R2 không về trình duyệt), ảnh mới gửi khoá vừa tải lên. */
export const requirementsToInput = (rows: RequirementDraft[]): CustomerRequirementsInput => ({
  entries: rows.map((r) => ({
    ...(r.id ? { id: r.id } : {}),
    category: r.category,
    title: r.title.trim() || undefined,
    description: r.description.trim() || undefined,
    photos: r.photos.map((p) =>
      p.id
        ? { id: p.id, fileName: p.fileName, fileSize: p.fileSize, mimeType: p.mimeType }
        : { key: p.key, fileName: p.fileName, fileSize: p.fileSize, mimeType: p.mimeType },
    ),
  })),
});

/**
 * Yêu cầu sản phẩm và bao bì của khách (yêu cầu v1.8 mục 2.3): nhiều mục; mỗi mục một nhóm (mác vận chuyển, kích thước bao bì, logo,
 * số lượng đóng gói, túi, thùng / bao ngoài, khác), tiêu đề, mô tả và ảnh kéo thả. Cùng một nhóm có thể có nhiều mục.
 */
export function CustomerRequirementsEditor({
  value,
  onChange,
  readOnly = false,
  onBusyChange,
}: {
  value: RequirementDraft[];
  onChange?: (next: RequirementDraft[]) => void;
  readOnly?: boolean;
  /** Tổng số ảnh đang tải lên ở mọi mục — trang khoá nút Lưu tới khi tải xong. */
  onBusyChange?: (uploading: number) => void;
}) {
  const { t } = useTranslation();
  const uploads = useRef(new Map<string, number>());
  const reportBusy = (localId: string, n: number) => {
    uploads.current.set(localId, n);
    onBusyChange?.([...uploads.current.values()].reduce((a, b) => a + b, 0));
  };
  const set = (localId: string, patch: Partial<RequirementDraft>) =>
    onChange?.(value.map((r) => (r.localId === localId ? { ...r, ...patch } : r)));

  if (readOnly && value.length === 0) return <p className="text-sm text-ink-faint">{t("evidence.reqEmpty")}</p>;

  return (
    <div className="space-y-3">
      {value.map((r) => (
        <div key={r.localId} className="rounded-sm border border-line bg-surface p-3">
          {readOnly ? (
            <div className="mb-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-brand">{t(`evidence.cat_${r.category}`)}</p>
              {r.title ? <p className="text-sm font-medium text-ink">{r.title}</p> : null}
              {r.description ? <p className="mt-1 whitespace-pre-wrap text-sm text-ink-soft">{r.description}</p> : null}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-[14rem_minmax(0,1fr)_auto]">
              <div>
                <FieldLabel htmlFor={`req-cat-${r.localId}`}>{t("evidence.reqCategory")}</FieldLabel>
                <Select
                  id={`req-cat-${r.localId}`}
                  containerClassName="w-full"
                  value={r.category}
                  onChange={(e) => set(r.localId, { category: e.target.value as CustomerRequirementCategory })}
                >
                  {CUSTOMER_REQUIREMENT_CATEGORY_KEYS.map((c) => (
                    <option key={c} value={c}>
                      {t(`evidence.cat_${c}`)}
                    </option>
                  ))}
                </Select>
                <p className="mt-1 text-2xs text-ink-faint">{t(`evidence.catHint_${r.category}`)}</p>
              </div>
              <div>
                <FieldLabel htmlFor={`req-title-${r.localId}`}>{t("evidence.reqEntryTitle")}</FieldLabel>
                <Input id={`req-title-${r.localId}`} maxLength={200} value={r.title} onChange={(e) => set(r.localId, { title: e.target.value })} />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => onChange?.(value.filter((x) => x.localId !== r.localId))}
                  aria-label={t("evidence.reqRemove")}
                  title={t("evidence.reqRemove")}
                >
                  <Trash2 className="size-3.5" aria-hidden="true" />
                </Button>
              </div>
              <div className="sm:col-span-3">
                <FieldLabel htmlFor={`req-desc-${r.localId}`}>{t("evidence.reqDescription")}</FieldLabel>
                <textarea
                  id={`req-desc-${r.localId}`}
                  rows={2}
                  maxLength={4000}
                  value={r.description}
                  onChange={(e) => set(r.localId, { description: e.target.value })}
                  className="w-full rounded-sm border border-line-strong bg-surface px-2.5 py-2 text-sm text-ink outline-none focus:border-brand"
                />
              </div>
            </div>
          )}
          <div className={readOnly ? "" : "mt-2"}>
            {!readOnly && <p className="mb-1 text-xs font-medium text-ink-soft">{t("evidence.reqPhotos")}</p>}
            <FileDropList
              files={r.photos}
              folder="customer-requirements"
              imagesOnly
              compact
              max={20}
              readOnly={readOnly}
              onBusyChange={(n) => reportBusy(r.localId, n)}
              onChange={(photos) => set(r.localId, { photos })}
            />
          </div>
          {readOnly && r.updatedAt ? (
            <p className="mt-2 text-2xs text-ink-faint">{t("evidence.reqUpdated", { at: formatDateTime(r.updatedAt), name: r.updatedByName ?? "—" })}</p>
          ) : null}
        </div>
      ))}
      {!readOnly && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          // Máy chủ nhận tối đa 50 mục (customerRequirementsSchema).
          disabled={value.length >= 50}
          onClick={() =>
            onChange?.([...value, { localId: crypto.randomUUID(), category: "shippingMarks", title: "", description: "", photos: [] }])
          }
        >
          <Plus className="size-3.5" aria-hidden="true" /> {t("evidence.reqAdd")}
        </Button>
      )}
    </div>
  );
}
